import { route } from 'ziggy-js';
import { resolveShopId } from '@/ShopManagement/resolveShopId.js';

/**
 * In-app notification inbox data-access layer.
 *
 * Framework-agnostic: owns the HTTP calls to the shop-management notification
 * endpoints and the Echo subscription for the signed-in user's own bell. Holds
 * no state — the store owns that.
 */

export class NotificationCenterError extends Error {
  constructor(code, message, fields = {}) {
    super(message);
    this.name = 'NotificationCenterError';
    this.code = code;
    this.fields = fields;
  }
}

const http = () => window.axios;

function endpoint(name, params = {}) {
  return route(`shop-management.notifications.${name}`, {
    shop_id: resolveShopId(),
    ...params,
  });
}

async function request(method, name, { params = {} } = {}) {
  try {
    const response = await http()({ method, url: endpoint(name, params) });

    return response.data?.data;
  } catch (error) {
    const data = error?.response?.data;
    if (data && data.code) {
      throw new NotificationCenterError(data.code, data.message, data.fields || {});
    }
    throw error;
  }
}

/**
 * The legacy AutoFlow bell endpoints live in a different route group
 * (`/shop/{shop_id}/notifications/...`), so they are built separately.
 */
function legacyEndpoint(name, params = {}) {
  return route(`notifications.${name}`, { shop_id: resolveShopId(), ...params });
}

export const notificationCenterService = Object.freeze({
  load: () => request('get', 'index'),

  markRead: (notificationId) =>
    request('post', 'read', { params: { notification: notificationId } }),

  markAllRead: () => request('post', 'read-all'),

  // ---- Legacy AutoFlow bell (websocket-backed, no per-item read state) ----
  /**
   * Marks one legacy notification read. `notify` is the legacy topic name and
   * is required by the endpoint.
   */
  markLegacyRead: (notificationId, notify) =>
    http()({
      method: 'post',
      url: legacyEndpoint('read', { id: notificationId }),
      data: { notify },
    }),

  clearLegacyAll: () => http()({ method: 'post', url: legacyEndpoint('clear-all') }),

  // ---- Realtime (Pusher/Echo) ----
  /**
   * Listen for the BayHiveNotificationCreated broadcast on the user's own
   * private channel. The leading dot matches broadcastAs() exactly, bypassing
   * Echo's default `App.Events` namespace. No-op when Echo is unavailable.
   */
  subscribe(channel, onCreated) {
    if (typeof window === 'undefined' || !window.Echo) return;
    window.Echo.private(channel).listen('.BayHiveNotificationCreated', onCreated);
  },

  leave(channel) {
    if (channel && typeof window !== 'undefined' && window.Echo) {
      window.Echo.leave(channel);
    }
  },
});
